import { MarkdownPlugin } from '@platejs/markdown'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { Plate, PlateContainer, PlateContent, usePlateEditor } from 'platejs/react'
import { useEffect } from 'react'
import Callout from '~/components/callout'
import { AppLoader } from '~/components/loader'
import { AutoformatKit } from '~/features/editor/plugins/autoformat-kit'
import { BasicMarksKit } from '~/features/editor/plugins/basic-marks-kit'
import { FloatingToolbarKit } from '~/features/editor/plugins/floating-toolbar-kit'
import { ListKit } from '~/features/editor/plugins/list-kit'
import { MarkdownKit } from '~/features/editor/plugins/markdown-kit'
import { RouterOutput, useTRPC } from '~/services/trpc'

import { Heading1 } from '@hap/components'
import { versionedScaleOptions } from '@hap/constants'
import { toPercent } from '@hap/utils'

export const Route = createFileRoute(
  '/_authenticated/$alias/print/evaluations/$evaluationId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const params = Route.useParams()
  const { data, isLoading } = useQuery(
    trpc.evaluations.fromId.queryOptions(params.evaluationId),
  )
  const editor = usePlateEditor({
    plugins: [
      ...BasicMarksKit,
      ...MarkdownKit,
      ...ListKit,
      ...AutoformatKit,
      ...FloatingToolbarKit,
    ],
    value: (editor) =>
      editor.getApi(MarkdownPlugin).markdown.deserialize(data?.notes ?? ''),
  })

  useEffect(() => {
    if (data) {
      editor.tf.setValue(
        editor.getApi(MarkdownPlugin).markdown.deserialize(data.notes ?? ''),
      )
      setTimeout(() => {
        window.print()
      }, 1000)
    }
  }, [data])

  if (isLoading) return <AppLoader />
  if (!data)
    return (
      <div className="p-3">
        <Callout title="Missing evaluation" />
      </div>
    )

  return (
    <div className="space-y-4 p-4 text-[10px]">
      <header>
        <Heading1 className="mb-3">{data.scorecard.name}</Heading1>

        <table>
          <tr>
            <td className="w-[100px] p-1 pl-0">Name</td>
            <td className="w-[300px] border-b border-b-black p-1">
              {data.assignedTo?.firstName} {data.assignedTo?.lastName}
            </td>
          </tr>
          <tr>
            <td className="p-1 pl-0">Evaluated on</td>
            <td className="border-b border-b-black p-1">
              {format(new Date(data.createdAt), 'MMM d, yyyy')}
            </td>
          </tr>
        </table>
      </header>

      {data.sections.map((section) => {
        return (
          <EvaluationSection
            key={section.id}
            id={section.id}
            name={section.name}
            criteria={data.criteria}
            scores={data.scores}
          />
        )
      })}
      <table className="border-border-dark mb-3 w-full border">
        <tbody>
          <tr>
            <td className="p-2 text-left">
              <h2 className="font-bold">Total</h2>
            </td>
            <td className="w-[60px]"></td>
            <td className="w-[60px] text-right font-bold">{toPercent(data.score)}</td>
            <td className="w-[60px]" />
          </tr>
        </tbody>
      </table>

      <div className="min-h-[200px] border">
        <header className="border-b px-4 py-2">
          <h2 className="font-bold">Comments</h2>
        </header>
        <div className="p-4">
          <Plate editor={editor}>
            <PlateContainer>
              <PlateContent style={{ minHeight: '100px', padding: '1em' }} readOnly />
            </PlateContainer>
          </Plate>
        </div>
      </div>
    </div>
  )
}

function EvaluationSection(props: {
  name: string
  id: string
  criteria: RouterOutput['evaluations']['fromId']['criteria']
  scores: RouterOutput['evaluations']['fromId']['scores']
}) {
  return (
    <table className="border-border-dark mb-3 w-full border">
      <thead className="bg-accent border-b-border-dark border-b">
        <tr>
          <th className="p-1 text-left">
            <h2 className="font-bold">{props.name}</h2>
          </th>
          <th>Weight</th>
          <th>Score</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {props.criteria
          .filter((c) => c.sectionId === props.id)
          .map((criterion) => {
            const score = props.scores.find(
              (s) => s.scorecardCriteriaId === criterion.criteriaId,
            )
            const ratingOptions = versionedScaleOptions[20250231][criterion.scale]
            const rating = ratingOptions.find((o) => o.value === score?.awardedPoints)
            return (
              <tr
                key={criterion.id}
                className="border-b-border-dark border-b px-4 py-2 last:border-b-0"
              >
                <td className="p-1">{criterion.name}</td>
                <td className="w-[60px] p-1 text-center">{criterion.weight}</td>
                <td className="w-[60px] p-1 text-right">{toPercent(score?.score)}</td>
                <td className="w-[60px] p-1 text-center">
                  {rating?.label ? (
                    <span className="text-[15px]">{rating.label}</span>
                  ) : (
                    'N/A'
                  )}
                </td>
              </tr>
            )
          })}
      </tbody>
    </table>
  )
}
